import { NULL_SUMMARY_MODE } from '@thoughtspot/formatter-service';
import { translate } from '@thoughtspot/i18n';
import _ from 'lodash';
import { getAxisConfig, getChartType, getPivotSummariesState, getPivotSummaryMode, getSortedColumnsId, getSortedColumnsIdOfType, getUseTreeLayout, hasOnlyAttributesInPivotValues, isFirstYAxisColumn, isYAxisColumn, } from '../../../core/chart-model-utils';
import { configOptions } from '../../../core/chart-type-specification-service';
import { containsMeasureNamesOrMeasureValuesColumns } from '../../../core/measure-names-and-values';
import { hasEmptyRowOrColumn } from '../../../devextreme/components/pivot.component.utils';
import { SummaryModeStringPath } from '../../../devextreme/model/pivot-data-model';
import { AccessMode, ChartType, } from '../../../types';
export const isFirstAxisOfType = (chartModel, currentAxis) => {
    const sortedColumnsIds = getSortedColumnsIdOfType(chartModel, currentAxis.properties.axisType);
    return currentAxis.properties.linkedColumns.includes(sortedColumnsIds[0]);
};
export const isFirstAxis = (chartModel, currentAxis) => {
    const sortedColumnsIds = getSortedColumnsId(chartModel);
    return currentAxis.properties.linkedColumns.includes(sortedColumnsIds[0]);
};
export const isLineSeriesColumn = (chartModel, columnId) => {
    if (!columnId)
        return true;
    const chartType = getChartType(chartModel);
    if (chartType === ChartType.LineStackedColumn ||
        chartType === ChartType.LineColumn) {
        if (isFirstYAxisColumn(chartModel, columnId)) {
            return false;
        }
    }
    return isYAxisColumn(chartModel, columnId);
};
export const MAPBOX_TILE_LABELS = {
    LIGHT: 'Light',
    DARK: 'Dark',
    OUTDOORS: 'Outdoors',
    STREETS: 'Streets',
    SATELLITE: 'Satellite',
    SATELLITE_STREETS: 'Satellite streets',
};
export const determineSummaryVisibility = (areMeasureNamesOrMeasureValuesColumnPresent, options) => {
    return areMeasureNamesOrMeasureValuesColumnPresent
        ? false
        : options.isSummaryEnabledByDefault;
};
export const isSummaryChecked = (summaryState, areMeasureNamesOrMeasureValuesColumnPresent, enforceToShowSummaries, options) => {
    return ((_.isNil(summaryState)
        ? determineSummaryVisibility(areMeasureNamesOrMeasureValuesColumnPresent, options)
        : !!summaryState) || enforceToShowSummaries);
};
export const areGrandTotalsChecked = (pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, options) => {
    return (isSummaryChecked(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowGrandTotals, areMeasureNamesOrMeasureValuesColumnPresent, enforceToShowSummaries, options) &&
        isSummaryChecked(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnGrandTotals, areMeasureNamesOrMeasureValuesColumnPresent, enforceToShowSummaries, options));
};
export const isRowGrandTotalsChecked = (pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, options) => {
    return isSummaryChecked(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowGrandTotals, areMeasureNamesOrMeasureValuesColumnPresent, enforceToShowSummaries, options);
};
export const isColumnGrandTotalsChecked = (pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, options) => {
    return isSummaryChecked(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnGrandTotals, areMeasureNamesOrMeasureValuesColumnPresent, enforceToShowSummaries, options);
};
export const areSubTotalsChecked = (pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, options) => {
    return (isSummaryChecked(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowTotals, areMeasureNamesOrMeasureValuesColumnPresent, enforceToShowSummaries, options) &&
        isSummaryChecked(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnTotals, areMeasureNamesOrMeasureValuesColumnPresent, enforceToShowSummaries, options));
};
export const isRowSubTotalsChecked = (isTreeLayoutEnabled, pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, options) => {
    return ((isTreeLayoutEnabled || _.isNil(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowTotals)
        ? determineSummaryVisibility(areMeasureNamesOrMeasureValuesColumnPresent, options)
        : !!(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowTotals)) || enforceToShowSummaries);
};
export const isColumnSubTotalsChecked = (pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, options) => {
    return ((_.isNil(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnTotals)
        ? determineSummaryVisibility(areMeasureNamesOrMeasureValuesColumnPresent, options)
        : !!(pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnTotals)) || enforceToShowSummaries);
};
var PivotTotalType;
(function (PivotTotalType) {
    PivotTotalType["RowSubTotals"] = "rowSubTotals";
    PivotTotalType["ColumnSubTotals"] = "columnSubTotals";
    PivotTotalType["RowGrandTotals"] = "rowGrandTotals";
    PivotTotalType["ColumnGrandTotals"] = "columnGrandTotals";
})(PivotTotalType || (PivotTotalType = {}));
const isPivotTotalEnabled = (chartModel, totalType) => {
    const axisConfig = getAxisConfig(chartModel);
    const pivotSummariesState = getPivotSummariesState(chartModel);
    const enforceToShowSummaries = hasEmptyRowOrColumn(axisConfig);
    const areMeasureNamesOrMeasureValuesColumnPresent = containsMeasureNamesOrMeasureValuesColumns(axisConfig);
    const chartConfigOptions = configOptions(getChartType(chartModel), chartModel);
    switch (totalType) {
        case PivotTotalType.RowSubTotals:
            return isRowSubTotalsChecked(getUseTreeLayout(chartModel), pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, chartConfigOptions);
        case PivotTotalType.ColumnSubTotals:
            return isColumnSubTotalsChecked(pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, chartConfigOptions);
        case PivotTotalType.RowGrandTotals:
            return isRowGrandTotalsChecked(pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, chartConfigOptions);
        case PivotTotalType.ColumnGrandTotals:
            return isColumnGrandTotalsChecked(pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, chartConfigOptions);
        default:
            return false;
    }
};
export const isRowSubTotalsEnabled = (chartModel) => isPivotTotalEnabled(chartModel, PivotTotalType.RowSubTotals);
export const isColumnSubTotalsEnabled = (chartModel) => isPivotTotalEnabled(chartModel, PivotTotalType.ColumnSubTotals);
export const isRowGrandTotalsEnabled = (chartModel) => isPivotTotalEnabled(chartModel, PivotTotalType.RowGrandTotals);
export const isColumnGrandTotalsEnabled = (chartModel) => isPivotTotalEnabled(chartModel, PivotTotalType.ColumnGrandTotals);
export const canToggleColumnSummary = (permission) => (permission === null || permission === void 0 ? void 0 : permission.dataSourceAccessLevel) !== AccessMode.NoAccess;
export const isSummaryModeNotNull = (requiredClientStateProperties) => {
    return ((requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.summaryMode) !== null &&
        (requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.summaryMode) !== NULL_SUMMARY_MODE);
};
export const getPivotSummariesDisabledConditions = (chartModel, requiredClientStateProperties) => {
    const axisConfig = getAxisConfig(chartModel);
    const hasEmptyRowCol = hasEmptyRowOrColumn(axisConfig);
    const hasOnlyAttributes = hasOnlyAttributesInPivotValues(chartModel);
    const hasSummaryMode = isSummaryModeNotNull(requiredClientStateProperties);
    return {
        hasEmptyRowCol,
        hasOnlyAttributes,
        hasSummaryMode,
        isDisabled: hasEmptyRowCol || hasOnlyAttributes || hasSummaryMode,
    };
};
export const getSummaryModeDisabledTooltip = (props) => {
    const { chartModel, requiredClientStateProperties } = props;
    const { hasEmptyRowCol, hasOnlyAttributes, hasSummaryMode, } = getPivotSummariesDisabledConditions(chartModel, requiredClientStateProperties);
    if (hasEmptyRowCol) {
        return translate('charts.chartEditor.pivotTable.tooltip.ENFORCE_TO_SHOW_SUMMARIES');
    }
    if (hasOnlyAttributes) {
        return translate('charts.chartEditor.pivotTable.tooltip.ENFORCE_TO_HIDE_SUMMARIES');
    }
    if (hasSummaryMode) {
        return translate('charts.chartEditor.pivotTable.tooltip.GRAND_TOTALS_SUMMARY_MODE_ENABLED');
    }
    return '';
};
export const isPivotSummariesDisabled = (props) => {
    const { chartModel, requiredClientStateProperties } = props;
    const { isDisabled } = getPivotSummariesDisabledConditions(chartModel, requiredClientStateProperties);
    return {
        disabled: isDisabled,
        tooltip: getSummaryModeDisabledTooltip(props),
    };
};
export const getSummaryModeTooltipForSubtotal = (chartModel, requiredClientStateProperties) => {
    if (!isSummaryModeNotNull(requiredClientStateProperties)) {
        return '';
    }
    const summaryModeLabel = translate(SummaryModeStringPath[getPivotSummaryMode(chartModel)]);
    return translate('charts.chartEditor.pivotTable.tooltip.GRAND_TOTALS_SUMMARY_MODE_ENABLED', { summaryModeLabel });
};
export const isHeatmapModeEnabled = (axisConfig, requiredClientStateProperties) => {
    return (containsMeasureNamesOrMeasureValuesColumns(axisConfig) &&
        ((requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.useFlatLayout) === undefined
            ? false
            : !(requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.useFlatLayout)));
};
export const getGradientRange = (chartModel, columnId, defaultColors) => {
    const data = _.get(chartModel, 'data[0].columnDataLite', []);
    const columnData = _.get(_.find(data, {
        columnId,
    }), 'dataValue');
    const min = _.toNumber(_.min(columnData));
    const max = _.toNumber(_.max(columnData));
    const mid = (min + max) / 2;
    return [
        +min.toFixed(2),
        defaultColors[1] !== '' ? +mid.toFixed(2) : null,
        +max.toFixed(2),
    ];
};
export const updateDataLabelProperties = (value, clientState) => {
    return {
        ...clientState,
        dataLabelProperties: {
            ...Object.fromEntries(Object.entries(clientState.dataLabelProperties).map(([columnId, column]) => [
                columnId,
                column && typeof column === 'object'
                    ? { ...column, isVisible: value }
                    : column,
            ])),
            allLabels: value,
        },
    };
};
//# sourceMappingURL=common-utils.js.map